import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AuthService, OccEndpointsService, StateUtils, StateWithProcess } from '@spartacus/core';
import { EMPTY, Observable, timer } from 'rxjs';
import { debounce, distinctUntilChanged, filter, map, mergeMap, share, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { ActiveCartFacade } from '../../../features/cart/base';
import { UserIdService } from '../../auth';
import { SearchAction } from '../../features/store/actions/search.actions';
import {
  Article,
  CartType,
  LoaderError,
  MiniShoppingList,
  MiniShoppingListsSearchParams,
  OrderEntry,
  Pagination,
  PrincipalSharedShoppingListFlags,
  PrincipalSharedShoppingListSearchParams,
  SearchParams,
  ShoppingList,
  ShoppingListPermission,
  Unit,
  UpdatePermissionType,
  UpdateShoppingListPermissionsParams,
  WritableShoppingListsSearchParams,
} from '../../model';
import { ProcessSelectors } from '../../process/store';
import { withdrawOn } from '../../util';
import { ShoppingListFacade } from '../facades';
import { UserActions } from '../store/actions';
import { UserSelectors } from '../store/selectors';
import { ShoppingListsSelectors } from '../store/selectors/user.selectors';
import {
  DELETE_SHOPPING_LIST_PROCESS,
  MINI_SHOPPING_LIST,
  MiniShoppingListSearchParamsState,
  StateWithUser,
  UPDATE_SHOPPING_LIST_CONTACT_PERSON_PROCESS,
  UPDATE_SHOPPING_LIST_DESCRIPTION_PROCESS,
  UPDATE_SHOPPING_LIST_NAME_PROCESS,
  WRITABLE_SHOPPING_LIST,
  WritableShoppingListSearchParamsState,
} from '../store/user-state';

@Injectable()
export class ShoppingListService implements ShoppingListFacade {
  constructor(
    private store: Store<StateWithUser & StateWithProcess<void>>,
    private userIdService: UserIdService,
    private authService: AuthService,
    private activeCartService: ActiveCartFacade,
    private httpClient: HttpClient,
    private occEndpointsService: OccEndpointsService
  ) {}

  // Mini shopping lists
  searchMiniShoppingLists(searchParams: MiniShoppingListsSearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.SearchMiniShoppingLists({ userId, searchParams })),
      () => {}
    );
  }

  updateSearchParamsForMiniShoppingLists(searchParams: MiniShoppingListsSearchParams): void {
    this.store.dispatch(new SearchAction(searchParams, MINI_SHOPPING_LIST));
  }

  getMiniShoppingListsSearchResult(key: string): Observable<MiniShoppingList[] | undefined> {
    return this.store.select(ShoppingListsSelectors.getSearchResultEntries(key));
  }

  getMiniShoppingListsSearchParams(key: string): Observable<MiniShoppingListSearchParamsState | undefined> {
    return this.store.select(ShoppingListsSelectors.getSearchParamsSelector(key)).pipe(map((params) => params as SearchParams));
  }

  getMiniShoppingListsSearchResultPagination(key: string): Observable<Pagination | undefined> {
    return this.store.select(ShoppingListsSelectors.getSearchPaginationSelector(key));
  }

  getMiniShoppingListsSearching(key: string): Observable<boolean> {
    return this.store.select(ShoppingListsSelectors.getSearchPageResultsLoading(key));
  }

  resetMiniShoppingListsState(): void {
    this.store.dispatch(new UserActions.ResetMiniShoppingLists());
  }

  // Writable shopping lists
  searchWritableShoppingLists(searchParams: WritableShoppingListsSearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.SearchWritableShoppingLists({ userId, searchParams })),
      () => {}
    );
  }

  updateSearchParamsForWritableShoppingLists(searchParams: WritableShoppingListsSearchParams): void {
    this.store.dispatch(new SearchAction(searchParams, WRITABLE_SHOPPING_LIST));
  }

  getWritableShoppingListsSearchResult(key: string): Observable<MiniShoppingList[] | undefined> {
    return this.store.select(ShoppingListsSelectors.getWritableSearchResultEntries(key));
  }

  getWritableShoppingListsSearchParams(key: string): Observable<WritableShoppingListSearchParamsState | undefined> {
    return this.store
      .select(ShoppingListsSelectors.getWritableSearchParamsSelector(key))
      .pipe(map((params) => params as SearchParams));
  }

  getWritableShoppingListsSearchResultPagination(key: string): Observable<Pagination | undefined> {
    return this.store.select(ShoppingListsSelectors.getWritableSearchPaginationSelector(key));
  }

  getWritableShoppingListsSearching(key: string): Observable<boolean> {
    return this.store.select(ShoppingListsSelectors.getWritableSearchPageResultsLoading(key));
  }

  private loadShoppingList(code: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadShoppingList({ userId, code })),
      () => {}
    );
  }

  getShoppingList(code: string, shoppingListKey?: string): Observable<ShoppingList | undefined> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.getShoppingListState(code)),
      withLatestFrom(this.getMiniShoppingListsSearchResult(shoppingListKey)),
      filter(([loaderState, miniShoppingListsSearchResult]) => {
        let miniShoppingListsSearchResultIncludesCode = true;
        if (!!shoppingListKey) {
          const miniShoppingListsSearchResultCodes = miniShoppingListsSearchResult?.map((shoppingList) => shoppingList.code);
          miniShoppingListsSearchResultIncludesCode = miniShoppingListsSearchResultCodes?.includes(code);
        }
        if (this.needsToLoad(loaderState) && miniShoppingListsSearchResultIncludesCode) {
          this.loadShoppingList(code);
          return false;
        }
        return loaderState.success;
      }),
      map(([loaderState]) => loaderState.value),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  private getShoppingListState(code: string): Observable<StateUtils.ProcessesLoaderState<ShoppingList>> {
    return this.store.select(ShoppingListsSelectors.getSelectedShoppingListStateFactory(code));
  }

  getShoppingListLoading(code: string): Observable<boolean> {
    return this.store.select(ShoppingListsSelectors.getSelectedShoppingListLoadingFactory(code));
  }

  getShoppingListLoaded(code: string): Observable<boolean> {
    return this.store.select(ShoppingListsSelectors.getSelectedShoppingListLoadedFactory(code));
  }

  updateShoppingListName(code: string, name: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.UpdateShoppingListName({ userId, code, name })),
      () => {}
    );
  }

  updateShoppingListNameSuccess(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessSuccessFactory(UPDATE_SHOPPING_LIST_NAME_PROCESS)));
  }

  updateShoppingListNameLoading(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessLoadingFactory(UPDATE_SHOPPING_LIST_NAME_PROCESS)));
  }

  resetUpdateShoppingListNameState(): void {
    this.store.dispatch(new UserActions.ResetUpdateShoppingListName());
  }

  updateShoppingListDescription(code: string, description: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.UpdateShoppingListDescription({ userId, code, description })),
      () => {}
    );
  }

  updateShoppingListDescriptionSuccess(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessSuccessFactory(UPDATE_SHOPPING_LIST_DESCRIPTION_PROCESS)));
  }

  updateShoppingListDescriptionLoading(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessLoadingFactory(UPDATE_SHOPPING_LIST_DESCRIPTION_PROCESS)));
  }

  resetUpdateShoppingListDescriptionState(): void {
    this.store.dispatch(new UserActions.ResetUpdateShoppingListDescription());
  }

  updateShoppingListContactPerson(code: string, customerId: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => {
        this.store.dispatch(new UserActions.UpdateShoppingListContactPerson({ userId, code, customerId }));
      },
      () => {}
    );
  }

  deleteShoppingList(code: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.DeleteShoppingList({ userId, code })),
      () => {}
    );
  }

  deleteShoppingListLoading(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessLoadingFactory(DELETE_SHOPPING_LIST_PROCESS)));
  }

  updateShoppingListContactPersonLoading(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessLoadingFactory(UPDATE_SHOPPING_LIST_CONTACT_PERSON_PROCESS)));
  }

  updateShoppingListContactPersonSuccess(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessSuccessFactory(UPDATE_SHOPPING_LIST_CONTACT_PERSON_PROCESS)));
  }

  resetUpdateShoppingListContactPerson(): void {
    this.store.dispatch(new UserActions.ResetUpdateShoppingListContactPerson());
  }

  deleteShoppingListError(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(ProcessSelectors.getProcessErrorFactory(DELETE_SHOPPING_LIST_PROCESS)));
  }

  deleteShoppingListSuccess(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessSuccessFactory(DELETE_SHOPPING_LIST_PROCESS)));
  }

  resetDeleteShoppingListState(): void {
    this.store.dispatch(new UserActions.ResetDeleteShoppingList());
  }

  createShoppingList(name: string, description: string): Observable<string> {
    const tempCode = this.generateTempCode();

    return this.userIdService.takeUserId(true).pipe(
      map((userId) => {
        this.store.dispatch(new UserActions.CreateShoppingList({ userId, tempCode, name, description }));
        return tempCode;
      })
    );
  }

  createShoppingListFromCart(cartType: CartType, name: string, description: string): Observable<string> {
    const tempCode = this.generateTempCode();

    return this.authService.isUserLoggedIn().pipe(
      filter(Boolean),
      withLatestFrom(this.userIdService.takeUserId(true), this.activeCartService.getActiveCartId(cartType)),
      take(1),
      map(([_, userId, cartId]) => {
        this.store.dispatch(
          new UserActions.CreateShoppingList({
            userId,
            tempCode,
            cartCode: cartId,
            name,
            description,
          })
        );

        return tempCode;
      })
    );
  }

  createShoppingListFromAnotherShoppingList(code: string, name: string, description: string): Observable<string> {
    const tempCode = this.generateTempCode();

    return this.userIdService.takeUserId(true).pipe(
      map((userId) => {
        this.store.dispatch(new UserActions.CreateShoppingList({ userId, tempCode, cloneCode: code, name, description }));
        return tempCode;
      })
    );
  }

  createShoppingListSuccess(id: string): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessSuccessFactory(id)));
  }

  createShoppingListAndAddEntry(
    name: string,
    description: string,
    articleCode: string,
    quantity: number,
    unit: Unit,
    displayNumber?: string
  ): Observable<string> {
    const tempCode = this.generateTempCode();

    return this.userIdService.takeUserId(true).pipe(
      map((userId) => {
        this.store.dispatch(
          new UserActions.CreateShoppingListAndAddEntry({
            userId,
            tempCode,
            name,
            description,
            articleCode,
            quantity,
            unit,
            displayNumber,
          })
        );
        return tempCode;
      })
    );
  }

  searchShoppingListPrincipals(code: string, searchParams: PrincipalSharedShoppingListSearchParams): void {
    this.userIdService
      .takeUserId(true)
      .subscribe((userId) => this.store.dispatch(new UserActions.LoadShoppingListPrincipals(userId, code, searchParams)));
  }

  getShoppingListPrincipals(
    searchParams: PrincipalSharedShoppingListSearchParams
  ): Observable<PrincipalSharedShoppingListFlags[]> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(ShoppingListsSelectors.getShoppingListPrincipalsSearchResultEntries(searchParams))),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getShoppingListPrincipalsPagination(searchParams: PrincipalSharedShoppingListSearchParams): Observable<Pagination> {
    return this.store.select(
      UserSelectors.ShoppingListsSelectors.getShoppingListPrincipalsSearchPaginationSelector(searchParams)
    );
  }

  getShoppingListPrincipalsSearchResultsLoading(searchParams: SearchParams): Observable<boolean> {
    return this.store.select(UserSelectors.ShoppingListsSelectors.getPrincipalsSearchResultsLoading(searchParams));
  }

  getShoppingListPrincipalsSearchResultsSuccess(searchParams: SearchParams): Observable<boolean> {
    return this.store.select(UserSelectors.ShoppingListsSelectors.getPrincipalsSearchResultsSuccess(searchParams));
  }

  updatePermissions(
    code: string,
    permissions: ShoppingListPermission[],
    params?: UpdateShoppingListPermissionsParams,
    updatePermissionType: UpdatePermissionType = UpdatePermissionType.CONNECT
  ): void {
    this.resetUpdateShoppingListPermissionsState();

    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.UpdateShoppingListPermissions({
            userId,
            code,
            permissions,
            params,
            updatePermissionType,
          })
        ),
      () => {}
    );
  }

  getUpdatePermissionsSuccess(): Observable<boolean> {
    return this.store.select(UserSelectors.ShoppingListsSelectors.getUpdatePermissionsSuccess);
  }

  getUpdatePermissionsLoading(): Observable<boolean> {
    return this.store.select(UserSelectors.ShoppingListsSelectors.getUpdatePermissionsLoading);
  }

  resetUpdateShoppingListPermissionsState(): void {
    this.store.dispatch(new UserActions.ResetUpdateShoppingListPermissions());
  }

  resetShoppingListsState(): void {
    this.store.dispatch(new UserActions.ResetShoppingLists());
  }

  resetShoppingListState(code: string): void {
    this.store.dispatch(new UserActions.RemoveShoppingList({ code }));
  }

  isStable(code: string): Observable<boolean> {
    return this.store.pipe(
      select(ShoppingListsSelectors.getShoppingListIsStableFactory(code)),
      debounce((isStable) => (isStable ? timer(0) : EMPTY)),
      distinctUntilChanged()
    );
  }

  addEntry(code: string, articleCode: string, quantity: number, unit: Unit, note?: string, displayNumber?: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.AddShoppingListEntry({
            userId,
            code,
            articleCode,
            quantity,
            unit,
            note,
            displayNumber,
          })
        ),
      () => {}
    );
  }

  updateEntry(code: string, entry: OrderEntry, quantity: number, unit: Unit): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => {
        if (quantity > 0) {
          this.store.dispatch(
            new UserActions.UpdateShoppingListEntry({
              userId,
              code,
              entry,
              quantity,
              unit,
            })
          );
        } else {
          this.removeEntry(code, entry);
        }
      },
      () => {}
    );
  }

  updateEntryNote(code: string, entry: OrderEntry, note: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.UpdateShoppingListEntry({
            userId,
            code,
            entry,
            quantity: undefined,
            unit: undefined,
            note,
          })
        ),
      () => {}
    );
  }

  updateEntryStatisticsCode(code: string, entry: OrderEntry, statisticsCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.UpdateShoppingListEntry({
            userId,
            code,
            entry,
            quantity: undefined,
            unit: undefined,
            statisticsCode,
          })
        ),
      () => {}
    );
  }

  replaceEntry(code: string, entry: OrderEntry, replacementArticle: Article): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.ReplaceShoppingListEntry({
            userId,
            code,
            entry,
            articleNumber: replacementArticle.code,
            quantity: replacementArticle.minimumOrderQuantity,
            unit: replacementArticle.unit,
          })
        ),
      () => {}
    );
  }

  removeEntry(code: string, entry: OrderEntry): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.RemoveShoppingListEntry({
            userId,
            code,
            entry,
          })
        ),
      () => {}
    );
  }

  removeEntries(code: string, entries: OrderEntry[]): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new UserActions.RemoveShoppingListEntries({
            userId,
            code,
            entries,
          })
        ),
      () => {}
    );
  }

  exportShoppingListAsPdf(code: string, displayPrices: boolean): Observable<Blob> {
    return this.userIdService
      .takeUserId(true)
      .pipe(mergeMap((userId) => this.download(userId, code, displayPrices, 'shoppingListPdf')));
  }

  exportShoppingListAsXlsx(code: string, displayPrices: boolean): Observable<Blob> {
    return this.userIdService
      .takeUserId(true)
      .pipe(mergeMap((userId) => this.download(userId, code, displayPrices, 'shoppingListXlsx')));
  }

  private download(userId: string, shoppingListCode: string, displayPrices: boolean, endpoint: string): Observable<Blob> {
    return this.httpClient.get(
      this.occEndpointsService.buildUrl(endpoint, {
        urlParams: {
          userId,
          shoppingListCode,
        },
        queryParams: {
          displayPrices,
        },
      }),
      {
        responseType: 'blob',
      }
    );
  }

  private generateTempCode(): string {
    const pseudoUuid = Math.random().toString(36).substr(2, 9);
    return `shoppingList-${pseudoUuid}`;
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<ShoppingList | PrincipalSharedShoppingListFlags[]>) {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }
}
